import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

// Color config for stacked bars
const COLORS = {
  accepted: '#42a5f5', // blue
  completed: '#66bb6a', // green
};

// Truncate long org names for axis labels
const truncate = (str, max = 12) => (str && str.length > max ? str.slice(0, max - 1) + '…' : str || '');

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    const accepted = payload.find(p => p.dataKey === 'accepted_cases')?.value || 0;
    const completed = payload.find(p => p.dataKey === 'completed_cases')?.value || 0;
    const acceptancePct = total ? ((accepted / total) * 100).toFixed(1) : 0;
    const completionPct = total ? ((completed / total) * 100).toFixed(1) : 0;
    return (
      <Box sx={{ p: 1.5, bgcolor: '#fff', border: '1px solid #ccc', borderRadius: 1, minWidth: 180 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
        <Typography variant="caption" display="block">Total: {total}</Typography>
        <Typography variant="caption" display="block" color="primary.main">Accepted: {accepted} ({acceptancePct}%)</Typography>
        <Typography variant="caption" display="block" color="success.main">Completed: {completed} ({completionPct}%)</Typography>
      </Box>
    );
  }
  return null;
};

const OrganizationPerformanceChart = ({ title = 'Organization Performance' }) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const storedUser = localStorageService?.getItem?.('user') ?? null;
        const rolesRaw = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
        const roles = Array.isArray(rolesRaw) ? rolesRaw.map(r => String(r).toLowerCase()) : [];
        const isSuperAdmin = roles.includes('super admin');

        const params = {}; // currently no org filter; endpoint appears global
        // If later differentiation needed for Super Admin vs org-level, adapt here
        const targetEmployee_id_Roles = ['case officer', 'first respondent'];
        const needToPassUserId = roles.some(r => targetEmployee_id_Roles.includes(r));
        if (needToPassUserId) {
          const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
          if (employeeId) params.employee_id = employeeId;
        }

        const res = await DashboardServices.getDashboardOrganizationPerformance(params);
        const payload = res?.data ?? res;
        const dataArray = payload?.view?.data ?? payload?.data ?? [];

        const formatted = Array.isArray(dataArray)
          ? dataArray
            .map(item => ({
              organization_id: item.organization_id,
              name: item.organization_short_name || item.organization_name || 'Org',
              fullName: item.organization_name || item.organization_short_name || 'Org',
              total_cases: Number(item.total_cases) || 0,
              completed_cases: Number(item.completed_cases) || 0,
              accepted_cases: Number(item.accepted_cases) || 0,
            }))
            .sort((a, b) => b.total_cases - a.total_cases)
          : [];

        if (mounted) setChartData(formatted);
      } catch (err) {
        console.error('Failed to load organization performance', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  //if (loading) return <WidgetCard sx={{ minHeight: 340 }}><CenteredSpinner /></WidgetCard>;

  return (
    <WidgetCard sx={{ minHeight: 340 }}>
      <MainCardTitle>{title}</MainCardTitle>
      {chartData.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
          No data available
        </Typography>
      ) : (
        <Box sx={{ mt: 2 }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#666', fontSize: 11 }}
                interval={0}
                tickFormatter={(v) => truncate(v, 10)}
                angle={-30}
                textAnchor="end"
              />
              <YAxis tick={{ fill: '#666', fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
              <Legend wrapperStyle={{ bottom: 4 }} />
              {/* Grouped bars (side-by-side) for Accepted vs Completed */}
              <Bar
                dataKey="accepted_cases"
                name="Accepted"
                fill={COLORS.accepted}
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="completed_cases"
                name="Completed"
                fill={COLORS.completed}
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </WidgetCard>
  );
};

export default OrganizationPerformanceChart;
